/**
 * Accident Reports API Service
 * Handles communication with the SearchAccidentReports endpoint
 */

import { logger } from '../utils/logger';
import {
  SearchReportsRequest,
  SearchReportsResponse,
  SearchError,
} from '../types/accident-reports';
import { config } from '../config/runtime-config';

// API Configuration - from runtime config
const API_BASE_URL = config.REACT_APP_SAAED_API_BASE_URL;
const API_PATH = config.REACT_APP_SAAED_ADMIN_PATH;
const SEARCH_ENDPOINT = '/SearchAccidentReports';
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Validate required configuration
if (!API_BASE_URL) {
  throw new Error('REACT_APP_SAAED_API_BASE_URL is required in config.js file');
}
if (!API_PATH) {
  throw new Error('REACT_APP_SAAED_ADMIN_PATH is required in config.js file');
}

/**
 * Search for accident reports based on vehicle plate information and date range
 */
export async function searchAccidentReports(
  request: SearchReportsRequest
): Promise<SearchReportsResponse> {
  logger.ui('Searching accident reports with criteria:', request);

  const url = `${API_BASE_URL}${API_PATH}${SEARCH_ENDPOINT}`;
  logger.ui('API URL:', url);

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle non-200 responses
    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`API error (${response.status}):`, errorText);
      
      const error: SearchError = {
        message: `API returned error: ${response.status} ${response.statusText}`,
        statusCode: response.status,
        details: errorText,
      };
      throw error;
    }

    // Parse response
    const data: SearchReportsResponse = await response.json();
    
    logger.ui(`Found ${data.Count} accident reports`);
    logger.ui('Reports data:', data.Data);

    return data;

  } catch (error: any) {
    // Handle abort (timeout)
    if (error.name === 'AbortError') {
      logger.error('API request timed out');
      const timeoutError: SearchError = {
        message: 'Request timed out. Please try again.',
        details: error,
      };
      throw timeoutError;
    }

    // Handle network errors
    if (error instanceof TypeError) {
      logger.error('Network error:', error);
      const networkError: SearchError = {
        message: 'Network error. Please check your connection and try again.',
        details: error,
      };
      throw networkError;
    }

    // Handle already formatted errors
    if (error.message && typeof error.message === 'string') {
      logger.error('API error:', error);
      throw error;
    }

    // Handle unexpected errors
    logger.error('Unexpected error:', error);
    const unexpectedError: SearchError = {
      message: 'An unexpected error occurred. Please try again.',
      details: error,
    };
    throw unexpectedError;
  }
}

/**
 * Format date to YYYY-MM-DD format
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse date string from input[type="date"] to YYYY-MM-DD
 */
export function parseDateInput(dateString: string): string {
  // Input date is already in YYYY-MM-DD format
  return dateString;
}

/**
 * Validate date range
 */
export function validateDateRange(startDate: string, endDate: string): {
  valid: boolean;
  error?: string;
} {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today

  // Check if dates are valid
  if (isNaN(start.getTime())) {
    return { valid: false, error: 'Invalid start date' };
  }
  if (isNaN(end.getTime())) {
    return { valid: false, error: 'Invalid end date' };
  }

  // Check if start date is before end date
  if (start > end) {
    return { valid: false, error: 'Start date must be before end date' };
  }

  // Check if dates are not in the future
  if (start > today) {
    return { valid: false, error: 'Start date cannot be in the future' };
  }
  if (end > today) {
    return { valid: false, error: 'End date cannot be in the future' };
  }

  return { valid: true };
}

